import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Loader2, LogOut, MapPin } from "lucide-react";
import { toast } from "sonner";
import { errMsg, imgUrl } from "@/lib/api";
import { ambApi, ambToken, monthLabel } from "@/lib/ambApi";
import { useProducts } from "@/hooks/useProducts";
import { useSeo } from "@/lib/seo";
import { AmbLogin } from "@/components/ambassadors/AmbAuth";
import { AmbPosts } from "@/components/ambassadors/AmbPosts";
import { AmbMission } from "@/components/ambassadors/AmbMission";
import { MondialRelayPicker } from "@/components/site/MondialRelayPicker";

const input = "h-11 w-full rounded-xl border border-[#D9C8B3] bg-white px-4 text-sm text-[#141013] outline-none focus:border-[#7A5418]";
const card = "rounded-3xl border border-[#E3D5C3] bg-[#FBF8F3] p-6 md:p-8";
const STATUS = { "à valider": "En attente de validation par Ozentya", "validée": "Validée : ton colis est en préparation", "expédiée": "Expédiée" };

function CodeCard({ me }) {
  const copy = async () => { await navigator.clipboard?.writeText(me.promo_code); toast.success("Code copié"); };
  return (
    <section data-testid="amb-dash-code" className="rounded-3xl bg-[#141013] p-6 text-[#FBF8F3] md:p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#E5C88F]">Ton code promo personnel</p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <span data-testid="amb-dash-code-value" className="break-all font-mono text-3xl font-bold tracking-[0.12em] text-[#E5C88F] md:text-4xl">{me.promo_code || "—"}</span>
        {me.promo_code ? <button onClick={copy} data-testid="amb-dash-code-copy" className="flex items-center gap-2 rounded-full border border-[#E5C88F]/60 px-4 py-2 text-xs uppercase tracking-[0.16em] hover:bg-[#E5C88F] hover:text-[#141013]"><Copy className="h-4 w-4" /> Copier</button> : null}
      </div>
      <p className="mt-4 text-sm text-[#E9DFD2]">-{me.percent} % pour ta communauté sur ozentya.net, à saisir dans le champ « Code promo » au moment de la commande.</p>
      <p data-testid="amb-dash-code-uses" className="mt-2 text-sm text-[#E9DFD2]">Commandes payées avec ton code : <b className="text-[#E5C88F]">{me.code_uses}</b></p>
    </section>
  );
}

function ProductPick({ products, picked, toggle, locked }) {
  return (
    <ul className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {products.map((p) => {
        const on = picked.includes(p.slug);
        return (
          <li key={p.slug}>
            <button type="button" disabled={locked || (!on && picked.length >= 2)} onClick={() => toggle(p.slug)} data-testid={`amb-pick-${p.slug}`} aria-pressed={on}
              className={`relative flex h-full w-full flex-col overflow-hidden rounded-2xl border bg-white text-left transition-[border-color,opacity] disabled:opacity-50 ${on ? "border-2 border-[#7A5418]" : "border-[#E3D5C3] hover:border-[#7A5418]"}`}>
              <img src={imgUrl(p.image)} alt={p.name} loading="lazy" className="aspect-square w-full bg-[#F6F0E7] object-cover" />
              <span className="p-3 text-sm text-[#141013]">{p.name}</span>
              {on ? <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#7A5418] text-white"><Check className="h-4 w-4" /></span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Selection({ me, onSaved }) {
  const { data } = useProducts({ type: "unite" });
  const products = (Array.isArray(data) ? data : []).filter((p) => p.type === "unite" && p.active !== false);
  const sel = me.selection;
  const locked = !!sel && sel.status !== "à valider";
  const [picked, setPicked] = useState(sel?.products || []);
  const [addr, setAddr] = useState({ name: sel?.name || "", phone: sel?.phone || "", email: sel?.contact_email || me.email || "" });
  const [relay, setRelay] = useState(sel?.relay || null);
  const [pick, setPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggle = (slug) => setPicked((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : p.length < 2 ? [...p, slug] : p));
  const save = async (e) => {
    e.preventDefault();
    if (picked.length !== 2) return toast.error("Choisis exactement 2 soins.");
    if (!relay) return toast.error("Choisis ton point Mondial Relay pour recevoir tes soins.");
    setBusy(true);
    try { await ambApi.put("/ambassador/selection", { products: picked, name: addr.name, phone: addr.phone, contact_email: addr.email, relay }); toast.success("Ta sélection est envoyée à Ozentya"); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const f = (k, ph, ac) => <input required placeholder={ph} autoComplete={ac} value={addr[k]} disabled={locked} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} data-testid={`amb-addr-${k}`} className={input} />;
  return (
    <form onSubmit={save} data-testid="amb-dash-selection" className={card}>
      <h2 className="font-display text-2xl text-[#141013] md:text-3xl">Tes 2 soins offerts de {monthLabel(me.month)}</h2>
      <p className="mt-2 text-sm text-[#5E544D]">Choisis 2 soins à l'unité, sans limite de prix (les packs ne sont pas inclus). Tu peux modifier ton choix tant qu'Ozentya ne l'a pas validé.</p>
      {sel ? <p data-testid="amb-dash-selection-status" className="mt-4 inline-block rounded-full bg-[#E7D3C0] px-4 py-1.5 text-sm text-[#141013]">{STATUS[sel.status] || sel.status}</p> : null}
      <p data-testid="amb-dash-picked-count" className="mt-4 text-sm font-semibold text-[#7A5418]">{picked.length} / 2 sélectionné{picked.length > 1 ? "s" : ""}</p>
      <ProductPick products={products} picked={picked} toggle={toggle} locked={locked} />
      <h3 className="mt-8 font-display text-xl text-[#141013]">Ton point Mondial Relay</h3>
      <p className="mt-2 text-sm text-[#5E544D]">Tes 2 soins offerts sont envoyés gratuitement dans le point Mondial Relay (ou Locker) de ton choix, en France métropolitaine.</p>
      <button type="button" disabled={locked} onClick={() => setPick(true)} data-testid="amb-relay-choose"
        className="mt-4 flex w-full items-start gap-3 rounded-xl border border-dashed border-[#7A5418] bg-white px-4 py-3 text-left text-sm text-[#141013] transition-colors hover:bg-[#F6F0E7] disabled:opacity-70">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#7A5418]" />
        {relay ? <span data-testid="amb-relay-selected"><b>{relay.kind === "locker" ? "Locker" : "Point Relais"} · {relay.name}</b><br />{relay.address}, {relay.zip} {relay.city}{!locked ? <u className="ml-1 text-[#7A5418]">modifier</u> : null}</span>
          : <span>Choisir mon point Mondial Relay</span>}
      </button>
      <MondialRelayPicker open={pick} onOpenChange={setPick} onChoose={setRelay} />
      <h3 className="mt-8 font-display text-xl text-[#141013]">Mes coordonnées pour le retrait</h3>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {f("name", "Prénom et nom", "name")}{f("phone", "Téléphone", "tel")}
        <div className="md:col-span-2">{f("email", "Email", "email")}</div>
      </div>
      <p className="mt-2 text-xs text-[#5E544D]">Indique le nom figurant sur ta pièce d'identité, il te sera demandé au retrait. Ton téléphone et ton email servent à l'avis d'arrivée du colis Mondial Relay.</p>
      {!locked ? <button disabled={busy} data-testid="amb-dash-selection-save" className="mt-6 flex h-12 items-center gap-2 rounded-full bg-[#141013] px-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#FBF8F3] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{sel ? "Mettre à jour ma sélection" : "Valider ma sélection"}</button> : null}
    </form>
  );
}

function History({ me, names }) {
  if (!me.history?.length) return null;
  return (
    <section data-testid="amb-dash-history" className={card}>
      <h2 className="font-display text-2xl text-[#141013]">Historique de tes avantages</h2>
      <ul className="mt-4 divide-y divide-[#E3D5C3]">
        {me.history.map((h) => <li key={h.month} className="flex flex-wrap justify-between gap-2 py-3 text-sm text-[#141013]"><span className="capitalize">{monthLabel(h.month)}</span><span>{h.products.map((s) => names[s] || s).join(" + ")}</span><span className="text-[#5E544D]">{STATUS[h.status] || h.status}</span></li>)}
      </ul>
    </section>
  );
}

export default function AmbassadorSpace() {
  useSeo({ title: "Espace ambassadeur | Ozentya", description: "Espace réservé aux ambassadeurs Ozentya.", path: "/espace-ambassadeur", noindex: true });
  const qc = useQueryClient();
  const [logged, setLogged] = useState(!!ambToken.get());
  const { data: me, error, refetch } = useQuery({ queryKey: ["amb-me"], queryFn: () => ambApi.get("/ambassador/me").then((r) => r.data), enabled: logged, retry: false });
  const { data: all } = useProducts();
  const names = Object.fromEntries((Array.isArray(all) ? all : []).map((p) => [p.slug, p.name]));
  useEffect(() => { if (error?.response?.status === 401 || error?.response?.status === 403) { ambToken.clear(); setLogged(false); } }, [error]);
  const logout = () => { ambToken.clear(); qc.removeQueries({ queryKey: ["amb-me"] }); setLogged(false); };
  if (!logged) return <AmbLogin onLogged={() => { setLogged(true); refetch(); }} />;
  if (!me) return <main className="min-h-screen bg-[#F6F0E7] pt-40 text-center text-[#5E544D]">Chargement…</main>;
  return (
    <main data-testid="amb-dashboard" className="min-h-screen bg-[#F6F0E7] px-5 pb-20 pt-32 md:px-10">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#7A5418]">Espace ambassadeur</p>
            <h1 data-testid="amb-dash-hello" className="mt-2 font-display text-4xl text-[#141013] sm:text-5xl">Bonjour {me.first_name}</h1></div>
          <button onClick={logout} data-testid="amb-dash-logout" className="flex items-center gap-2 text-sm text-[#5E544D] underline underline-offset-4"><LogOut className="h-4 w-4" /> Me déconnecter</button>
        </div>
        <AmbMission />
        <CodeCard me={me} />
        <AmbPosts />
        <Selection key={me.selection?.updated_at || "new"} me={me} onSaved={() => qc.invalidateQueries({ queryKey: ["amb-me"] })} />
        <History me={me} names={names} />
      </div>
    </main>
  );
}
